import React from "react";
import { Box, Typography, Button, Divider } from "@mui/material";
import DownloadIcon from '@mui/icons-material/Download';
import Tag from "./Tag";
import ProjectCard from "./ProjectCard";

const Home = () => {
    const otherSkills = ['JavaScript', 'TypeScript','Python','Git', 'AWS', 'RESTful API', 'Docker','NumPy', 'Pandas', 'ROS', 'IoT', 'OTA', 'Scikit-Learn', 'PyTorch', 'TensorFlow', 'HTML', 'CSS', 'Terraform', 'CI/CD', 'AI Assisted Development'];
    const frontendSkills = ['React.JS', 'Next.JS', 'BootStrap', 'Material UI', 'Redux.JS',];
    const backendSkills = ['Node.JS', 'Express.JS', 'Flask', 'Alembic', 'PostgreSQL', 'MongoDB', 'Sequelize', 'Mongoose'];
    const language = ['Italian', 'Chinese (Mandarin)', 'English'];

    // Projects - reordered with Matrix (strongest) first
    const projects = [
        {
            imgUrl:'/static/Proximity.png',
            title:'Matrix Industries - Proximity',
            tags:['JavaScript', 'Python','React.JS', 'Flask','PostgreSQL', 'AWS', 'IoT'],
            description:'Full-stack IoT monitoring platform for real-time device data visualization. Built AI/ML-powered event detection system with AWS cloud infrastructure. Processes real-time sensor data and displays actionable insights through a React dashboard used by enterprise clients.',
            url:'https://dashboard-staging.proximity.matrixindustries.com/org/52/map',
            github:'https://github.com/liulc006'
        },
        {
            imgUrl:'/static/Perceptive.png',
            title:'Matrix Industries - Perceptive',
            tags:['JavaScript', 'Python','React.JS', 'Flask','PostgreSQL', 'AWS', 'IoT'],
            description:'Health monitoring platform for wearable IoT devices displaying real-time metrics including heart rate, activity tracking, and emergency alerts. Developed custom APIs enabling third-party integrations. System processes and visualizes health data for enterprise healthcare applications.',
            url:'https://api-dev.perceptive.matrixindustries.com/',
            github:'https://github.com/liulc006'
        },
        {
            imgUrl:'/static/postcard-project.png',
            title:'Postcard Generator',
            tags:['TypeScript', 'Next.JS', 'Gemini API','OpenAI API'],
            description:'AI-powered web app for creating and sharing personalized postcards. Features include photo uploads, custom messages, and AI-generated content using Gemini and OpenAI APIs. Built with modern React architecture and server-side rendering.',
            url:'https://postcard-s4co.onrender.com/',
            github:'https://github.com/liulc006/postcard'
        },
        {
            imgUrl:'/static/SWG.png',
            title:'Smile With Grace Dental Care',
            tags:['JavaScript', 'React.JS', 'Heroku'],
            description:'Professional website for dental clinic featuring contact forms, service showcases, and mobile-responsive design. Increased online presence and customer engagement for small business client.',
            url: 'https://www.smilewithgracedental.com/',
        },
        {
            imgUrl:'/static/PetFoodie.png',
            title:'Pet Foodie - E-Commerce',
            tags:['JavaScript', 'React.JS', 'PostgreSQL', 'Stripe', 'OAuth'],
            description:'Full-stack e-commerce platform with user authentication (OAuth), product catalog, and Stripe payment processing. PERN stack implementation with secure payment flows and social login integration.',
            github:'https://github.com/liulc006/Pet-Foodie',
        },
        {
            imgUrl:'/static/Goji.png',
            title:'GOJI - Dog Walker Service',
            tags:['JavaScript', 'React.JS', 'MongoDB', 'Google Maps API'],
            description:'Service marketplace connecting pet owners with caregivers. Dual-portal system for customers and providers with Google Maps integration for location display. PERN stack with real-time booking capabilities.',
            github:'https://github.com/liulc006/goji-capstone'
        },
    ]

    const work = [
        {
            title:'Matrix Industries Inc',
            position:'Full-Stack Engineer',
            description:'Full-stack engineer building and maintaining core application features across frontend and backend systems. Developed and optimized APIs, services, and user interfaces while ensuring reliable deployments and system performance. Supported production systems and collaborated cross-functionally to deliver scalable, business-aligned solutions.',
            startTime: 'September 2024',
            endTime: null,
        },
        {
            title:'Smile With Grace Dental Care',
            position:'Software Engineer (Freelance)',
            description:'Designed and built custom web applications for small business clients, owning the full lifecycle from requirements to deployment. Developed intuitive UIs, efficient backend services, and scalable databases for production-ready solutions. Collaborated directly with clients to deliver software aligned with business needs.',
            startTime: 'February 2023',
            endTime: 'August 2024',
        },         {
            title:'Chefman & ChefIQ',
            position:'Data Testing Engineer',
            description:'Data Testing Engineer at Chefman focused on validating data quality and accuracy across analytics systems. Built Python-based pipelines to automate data validation and generate insights. Partnered with product managers and engineers to improve data pipelines and ensure reliable, decision-ready data.',
            startTime: 'February 2021',
            endTime: 'November 2022',
        },
    ]

    return (
        <Box sx={{paddingTop:'4rem',display:'flex', flexDirection:'column', width:'100%'}}>
            {/* Hero Section */}
            <Box sx={{
                minHeight: '85vh',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '2rem',
                background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)'
            }}>
                <Box sx={{
                    width: '100%',
                    maxWidth: '1200px',
                    display: 'flex',
                    flexDirection: {xs: 'column-reverse', md: 'row'},
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '3rem'
                }}>
                    <Box sx={{
                        flex: '1.5',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: {xs: 'center', md: 'flex-start'},
                        textAlign: {xs: 'center', md: 'left'}
                    }}>
                        <Typography variant="h1" sx={{
                            background: 'linear-gradient(135deg, #0f172a 0%, #3b82f6 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            marginBottom: '0.5rem',
                            animation: 'fadeInUp 0.8s ease-out'
                        }}>
                            Luca Liu
                        </Typography>
                        <Typography variant="h2" sx={{
                            color: '#3b82f6',
                            fontWeight: 600,
                            fontSize: '1.75rem',
                            marginBottom: '1.5rem'
                        }}>
                            Full-Stack Software Engineer
                        </Typography>
                        <Typography variant="body" sx={{
                            color: '#475569',
                            maxWidth: '500px',
                            marginBottom: '2rem',
                            fontSize: '1.1rem'
                        }}>
                            I build scalable web applications and intuitive user interfaces that solve real-world problems.
                            Currently engineering IoT platforms and Full-Stack applications at <a href="https://www.matrixindustries.com" target="_blank" rel="noopener noreferrer" style={{color: '#3b82f6', textDecoration: 'none'}}>Matrix Industries</a>.
                        </Typography>
                        <Box sx={{display: 'flex', gap: '1rem', flexWrap: 'wrap'}}>
                            <Button
                                variant="contained"
                                component="a"
                                href="/static/resume.pdf"
                                download
                                sx={{
                                    backgroundColor: '#3b82f6',
                                    color: 'white',
                                    fontWeight: 600,
                                    padding: '0.85rem 2rem',
                                    borderRadius: '8px',
                                    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                                    '&:hover': {
                                        backgroundColor: '#1d4ed8',
                                        boxShadow: '0 6px 16px rgba(59, 130, 246, 0.4)',
                                        transform: 'translateY(-2px)'
                                    }
                                }}
                            >
                                <DownloadIcon sx={{marginRight: '0.5rem'}} />
                                Download Resume
                            </Button>
                            <Button
                                variant="outlined"
                                component="a"
                                href="mailto:lucaliu96@gmail.com"
                                sx={{
                                    borderColor: '#3b82f6',
                                    color: '#3b82f6',
                                    fontWeight: 600,
                                    padding: '0.85rem 2rem',
                                    borderRadius: '8px',
                                    '&:hover': {
                                        borderColor: '#1d4ed8',
                                        backgroundColor: 'rgba(59, 130, 246, 0.08)',
                                        transform: 'translateY(-2px)'
                                    }
                                }}
                            >
                                Contact Me
                            </Button>
                        </Box>
                    </Box>
                    <Box sx={{
                        flex: '1',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}>
                        <Box sx={{
                            width: '280px',
                            height: '280px',
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                            padding: '4px',
                            boxShadow: '0 20px 40px rgba(59, 130, 246, 0.3)'
                        }}>
                            <Box
                                component="img"
                                alt="Profile Picture"
                                src="/static/profilepic.jpg"
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    borderRadius: '50%',
                                    objectFit: 'cover',
                                    border: '4px solid #ffffff'
                                }}
                            />
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* About Me */}
            <Box id="aboutme" sx={{
                display: 'flex',
                flexDirection: 'column',
                padding: '4rem 2rem',
                backgroundColor: '#ffffff'
            }}>
                <Box sx={{maxWidth: '800px', margin: '0 auto', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%'}}>
                    <Typography variant="h2" sx={{
                        color: '#0f172a',
                        fontWeight: 700,
                        marginBottom: '1rem'
                    }}>About Me</Typography>
                    <Box sx={{
                        height: '4px',
                        width: '4rem',
                        background: 'linear-gradient(90deg, #3b82f6 0%, #1d4ed8 100%)',
                        borderRadius: '2px',
                        margin: '0 auto 2rem'
                    }}/>
                    <Typography variant="body" sx={{
                        color: '#475569',
                        lineHeight: 1.8,
                        marginBottom: '1.5rem',
                        fontSize: '1.05rem'
                    }}>
                        I am a <strong>Full-Stack Software Engineer</strong> with experience designing, developing, and deploying scalable web applications across both <strong>front-end and back-end systems</strong>.
                        I enjoy building intuitive user interfaces, developing robust APIs, and creating reliable, maintainable software using modern technologies.
                    </Typography>
                    <Typography variant="body" sx={{
                        color: '#475569',
                        lineHeight: 1.8,
                        fontSize: '1.05rem'
                    }}>
                        My background in <strong>Mechanical Engineering</strong> strengthened my analytical thinking and problem-solving skills, providing a strong foundation that I apply when tackling complex technical challenges in software development.
                    </Typography>
                </Box>
            </Box>

            {/* Work History */}
            <Box id="work-history" sx={{
                display: 'flex',
                flexDirection: 'column',
                padding: '4rem 2rem',
                backgroundColor: '#f8fafc'
            }}>
                <Box sx={{maxWidth: '800px', margin: '0 auto', width: '100%'}}>
                    <Typography variant="h2" sx={{
                        color: '#0f172a',
                        fontWeight: 700,
                        textAlign: 'center',
                        marginBottom: '1rem'
                    }}>Work History</Typography>
                    <Box sx={{
                        height: '4px',
                        width: '4rem',
                        background: 'linear-gradient(90deg, #3b82f6 0%, #1d4ed8 100%)',
                        borderRadius: '2px',
                        margin: '0 auto 2rem'
                    }}/>
                    {work.map((job) => (
                        <Box
                            key={`work-${job.title.toLowerCase().replace(/\s+/g, '-')}`}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                backgroundColor: '#ffffff',
                                borderRadius: '12px',
                                padding: '1.5rem',
                                marginBottom: '1rem',
                                border: '1px solid rgba(148, 163, 184, 0.2)',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    boxShadow: '0 8px 20px rgba(0,0,0,0.08)',
                                    transform: 'translateY(-2px)'
                                }
                            }}
                        >
                            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap'}}>
                                <Box>
                                    <Typography variant="h3" sx={{
                                        color: '#0f172a',
                                        fontWeight: 600,
                                        fontSize: '1.25rem'
                                    }}>{job.title}</Typography>
                                    <Typography variant="body" sx={{
                                        color: '#3b82f6',
                                        fontWeight: 500,
                                        marginTop: '0.25rem'
                                    }}>{job.position}</Typography>
                                </Box>
                                <Typography variant="note" sx={{
                                    color: '#64748b',
                                    whiteSpace: 'nowrap'
                                }}>{job.startTime} - {job.endTime ? job.endTime : 'Present'}</Typography>
                            </Box>
                            <Typography variant="body" sx={{
                                marginTop: '1rem',
                                color: '#475569',
                                lineHeight: 1.7
                            }}>{job.description}</Typography>
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* Education and Skills */}
            <Box id="skills" sx={{
                display: 'flex',
                flexDirection: 'column',
                padding: '4rem 2rem',
                backgroundColor: '#ffffff'
            }}>
                <Box sx={{
                    maxWidth: '1000px',
                    margin: '0 auto',
                    width: '100%',
                    display: 'flex',
                    flexDirection: {xs: 'column', md: 'row'},
                    gap: '3rem'
                }}>
                    <Box sx={{flex: '1'}}>
                        <Typography variant="h3" sx={{
                            color: '#0f172a',
                            fontWeight: 600,
                            marginBottom: '1.5rem'
                        }}>Education</Typography>
                        <Box sx={{display: 'flex', flexDirection: 'column', gap: '1.5rem'}}>
                            <Box sx={{display:'flex', flexDirection: 'column', gap: '0.25rem'}}>
                                <Typography variant="body" sx={{fontWeight: 700, color: '#0f172a', fontSize: '1.05rem'}}>Columbia University</Typography>
                                <Typography variant="body" sx={{color: '#475569'}}>M.S. Mechanical Engineering - Robotics and Control</Typography>
                                <Typography variant="note" sx={{color: '#64748b'}}>December 2020</Typography>
                            </Box>
                            <Box sx={{display:'flex', flexDirection: 'column', gap: '0.25rem'}}>
                                <Typography variant="body" sx={{fontWeight: 700, color: '#0f172a', fontSize: '1.05rem'}}>Boston University</Typography>
                                <Typography variant="body" sx={{color: '#475569'}}>B.S. Mechanical Engineering</Typography>
                                <Typography variant="note" sx={{color: '#64748b'}}>May 2019</Typography>
                            </Box>
                            <Box sx={{display:'flex', flexDirection: 'column', gap: '0.25rem'}}>
                                <Typography variant="body" sx={{fontWeight: 700, color: '#0f172a', fontSize: '1.05rem'}}>Fullstack Academy</Typography>
                                <Typography variant="body" sx={{color: '#475569'}}>Software Development Engineering Certificate</Typography>
                                <Typography variant="note" sx={{color: '#64748b'}}>December 2022</Typography>
                            </Box>
                        </Box>
                    </Box>
                    <Box sx={{flex: '1.2'}}>
                        <Typography variant="h3" sx={{
                            color: '#0f172a',
                            fontWeight: 600,
                            marginBottom: '1.5rem'
                        }}>Skills</Typography>
                        <Box sx={{marginBottom: '1.5rem'}}>
                            <Typography variant="body" sx={{fontWeight: 600, color: '#0f172a', marginBottom: '0.75rem'}}>Front End</Typography>
                            <Box sx={{display: 'flex', flexWrap: 'wrap'}}>
                                {frontendSkills.map(ele => <Tag key={ele} name={ele}/>)}
                            </Box>
                        </Box>
                        <Box sx={{marginBottom: '1.5rem'}}>
                            <Typography variant="body" sx={{fontWeight: 600, color: '#0f172a', marginBottom: '0.75rem'}}>Back End</Typography>
                            <Box sx={{display: 'flex', flexWrap: 'wrap'}}>
                                {backendSkills.map(ele => <Tag key={ele} name={ele}/>)}
                            </Box>
                        </Box>
                        <Box>
                            <Typography variant="body" sx={{fontWeight: 600, color: '#0f172a', marginBottom: '0.75rem'}}>Other</Typography>
                            <Box sx={{display: 'flex', flexWrap: 'wrap'}}>
                                {otherSkills.map(ele => <Tag key={ele} name={ele}/>)}
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* Projects */}
            <Box id="projects" sx={{
                display: 'flex',
                flexDirection: 'column',
                padding: '4rem 2rem',
                backgroundColor: '#f8fafc'
            }}>
                <Box sx={{maxWidth: '1000px', margin: '0 auto', width: '100%', textAlign: 'center'}}>
                    <Typography variant="h2" sx={{
                        color: '#0f172a',
                        fontWeight: 700,
                        marginBottom: '1rem'
                    }}>Projects</Typography>
                    <Box sx={{
                        height: '4px',
                        width: '4rem',
                        background: 'linear-gradient(90deg, #3b82f6 0%, #1d4ed8 100%)',
                        borderRadius: '2px',
                        margin: '0 auto 2rem'
                    }}/>
                    <Typography variant="body" sx={{
                        color: '#475569',
                        maxWidth: '600px',
                        margin: '0 auto 2rem',
                        lineHeight: 1.7
                    }}>
                        A selection of personal and client projects showcasing my expertise in full-stack development, from IoT platforms to AI-powered applications.
                    </Typography>
                </Box>
                <Box sx={{maxWidth: '1000px', margin: '0 auto', width: '100%'}}>
                    {projects.map((project) => (
                        <ProjectCard
                            key={`project-${project.title.toLowerCase().replace(/\s+/g, '-')}`}
                            imgUrl={project.imgUrl}
                            title={project.title}
                            tags={project.tags}
                            description={project.description}
                            url={project.url}
                            github={project.github}
                        />
                    ))}
                </Box>
            </Box>

            {/* Contact Me */}
            <Box id="contactme" sx={{
                display: 'flex',
                flexDirection: 'column',
                padding: '4rem 2rem',
                backgroundColor: '#ffffff',
                marginBottom: '4rem'
            }}>
                <Box sx={{maxWidth: '800px', margin: '0 auto', textAlign: 'center', width: '100%'}}>
                    <Typography variant="h2" sx={{
                        color: '#0f172a',
                        fontWeight: 700,
                        marginBottom: '1rem'
                    }}>Contact Me</Typography>
                    <Box sx={{
                        height: '4px',
                        width: '4rem',
                        background: 'linear-gradient(90deg, #3b82f6 0%, #1d4ed8 100%)',
                        borderRadius: '2px',
                        margin: '0 auto 2rem'
                    }}/>
                    <Typography variant="body" sx={{
                        color: '#475569',
                        lineHeight: 1.8,
                        marginBottom: '2rem',
                        fontSize: '1.05rem'
                    }}>
                        I'm always open to discussing new projects, creative ideas, or opportunities to be part of your visions.
                    </Typography>
                    <Box sx={{display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap'}}>
                        <Button
                            variant="contained"
                            component="a"
                            href="mailto:lucaliu96@gmail.com"
                            sx={{
                                backgroundColor: '#3b82f6',
                                color: 'white',
                                fontWeight: 600,
                                padding: '0.85rem 2rem',
                                borderRadius: '8px',
                                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                                '&:hover': {
                                    backgroundColor: '#1d4ed8',
                                    boxShadow: '0 6px 16px rgba(59, 130, 246, 0.4)',
                                    transform: 'translateY(-2px)'
                                }
                            }}
                        >
                            Send Email
                        </Button>
                        <Button
                            variant="outlined"
                            component="a"
                            href="https://github.com/liulc006"
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                borderColor: '#3b82f6',
                                color: '#3b82f6',
                                fontWeight: 600,
                                padding: '0.85rem 2rem',
                                borderRadius: '8px',
                                '&:hover': {
                                    borderColor: '#1d4ed8',
                                    backgroundColor: 'rgba(59, 130, 246, 0.08)',
                                    transform: 'translateY(-2px)'
                                }
                            }}
                        >
                            GitHub
                        </Button>
                        <Button
                            variant="outlined"
                            component="a"
                            href="https://www.linkedin.com/in/luca-liu/"
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                borderColor: '#3b82f6',
                                color: '#3b82f6',
                                fontWeight: 600,
                                padding: '0.85rem 2rem',
                                borderRadius: '8px',
                                '&:hover': {
                                    borderColor: '#1d4ed8',
                                    backgroundColor: 'rgba(59, 130, 246, 0.08)',
                                    transform: 'translateY(-2px)'
                                }
                            }}
                        >
                            LinkedIn
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default Home;
